import { Link, NavLink, Outlet } from "react-router-dom";
import { Shield, Menu, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCompanyInfo } from "@/hooks/useCompanyInfo";
import AIChatWidget from "@/components/AIChatWidget";
import LanguageSelector from "@/components/LanguageSelector";

export default function PublicLayout() {
  const [open, setOpen] = useState(false);
  const { company } = useCompanyInfo();
  const { t } = useTranslation();

  const nav = [
    { to: "/", label: t("nav.home") },
    { to: "/services", label: t("nav.services") },
    { to: "/track", label: t("nav.track") },
    { to: "/about", label: t("nav.about") },
    { to: "/contact", label: t("nav.contact") },
  ];

  return (
    <div className="min-h-screen bg-[#0A1128] text-slate-100 flex flex-col">
      <header
        className="sticky top-0 z-40 backdrop-blur-md bg-[#0A1128]/85 border-b border-amber-500/20"
        data-testid="public-navbar"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" data-testid="brand-link">
            <div className="w-10 h-10 rounded-md border border-amber-500/40 flex items-center justify-center bg-[#0F1C3F] group-hover:border-amber-400 transition-colors">
              <Shield className="w-5 h-5 text-amber-400" />
            </div>
            <div className="leading-tight">
              <div className="font-serif text-lg tracking-tight text-slate-50">
                Diplomatic Vaults
              </div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-amber-400/80 font-mono">
                {t("brand.suffix")}
              </div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-md text-sm transition-colors ${
                    isActive
                      ? "text-amber-400 bg-amber-500/10"
                      : "text-slate-300 hover:text-amber-300 hover:bg-amber-500/5"
                  }`
                }
                end={n.to === "/"}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <LanguageSelector />
            <button
              className="md:hidden text-amber-300 p-2"
              onClick={() => setOpen(!open)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {open && (
          <div className="md:hidden border-t border-amber-500/20 px-6 py-4 space-y-2 bg-[#0A1128]">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className="block text-slate-200 py-2"
              >
                {n.label}
              </NavLink>
            ))}
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-amber-500/15 bg-[#0A1128]" data-testid="public-footer">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-md border border-amber-500/40 flex items-center justify-center bg-[#0F1C3F]">
                <Shield className="w-4 h-4 text-amber-400" />
              </div>
              <div className="font-serif text-lg">Diplomatic Vaults {t("brand.suffix")}</div>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed max-w-md">
              {t("footer.description")}
            </p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-amber-400/80 font-mono mb-4">
              {t("footer.explore")}
            </div>
            <ul className="space-y-2 text-sm text-slate-300">
              <li><Link to="/services" className="hover:text-amber-300">{t("nav.services")}</Link></li>
              <li><Link to="/track" className="hover:text-amber-300">{t("nav.track")}</Link></li>
              <li><Link to="/about" className="hover:text-amber-300">{t("nav.about")}</Link></li>
              <li><Link to="/contact" className="hover:text-amber-300">{t("nav.contact")}</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-amber-400/80 font-mono mb-4">
              {t("footer.office")}
            </div>
            <address
              className="not-italic text-sm text-slate-400 leading-relaxed whitespace-pre-line"
              data-testid="footer-company-info"
            >
              {company.address}
              {"\n"}
              {company.phone}
              {"\n"}
              {company.secure_email}
            </address>
          </div>
        </div>
        <div className="border-t border-amber-500/10 py-6 text-center text-xs text-slate-500 font-mono uppercase tracking-widest">
          © {new Date().getFullYear()} Diplomatic Vaults {t("brand.suffix")} — {t("footer.rights")}
        </div>
      </footer>

      <AIChatWidget />
    </div>
  );
}
